/**
 * Records whose details were opened recently, kept on this device.
 *
 * Only the record's id is kept: never a name, email or phone. The search
 * fetches the records afresh by id through the scoped search endpoint, so
 * the names it shows are whatever this person may see now, and a record they
 * can no longer see simply does not come back.
 *
 * A new key; no existing `hawfinch_*` key is reused or renamed. Cleared on
 * sign-out. The iPhone app's mobile-ionic/src/search/shared/ holds an exact
 * copy, and a test there fails if the two differ.
 */

export const RECENT_LEADS_KEY = 'hawfinch_recent_leads';
export const RECENT_LEADS_MAX = 8;

/** A lead id as the server makes them (a UUID): letters, digits, - and _. */
const ID = /^[A-Za-z0-9_-]{1,64}$/;

/**
 * An id fit to keep, or null. It must have a letter in it, so a phone
 * number can never be stored as though it were an id.
 */
export function keepableId(id: unknown): string | null {
  if (typeof id !== 'string') return null;
  const t = id.trim();
  return ID.test(t) && /[A-Za-z]/.test(t) && !t.includes('@') ? t : null;
}

export function readRecentLeads(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_LEADS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const out: string[] = [];
    for (const x of parsed) {
      const id = keepableId(x);
      if (id && !out.includes(id)) out.push(id);
    }
    return out.slice(0, RECENT_LEADS_MAX);
  } catch {
    return [];
  }
}

/** Puts a record's id at the top of the list and returns the new list. */
export function pushRecentLead(id: string): string[] {
  const kept = keepableId(id);
  const current = readRecentLeads();
  if (!kept) return current;
  const next = [kept, ...current.filter((x) => x !== kept)].slice(0, RECENT_LEADS_MAX);
  try {
    localStorage.setItem(RECENT_LEADS_KEY, JSON.stringify(next));
  } catch {
    /* private mode or full storage: the list just is not remembered */
  }
  return next;
}

export function clearRecentLeads(): void {
  try {
    localStorage.removeItem(RECENT_LEADS_KEY);
  } catch {
    /* private mode */
  }
}
